import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ServicePageLayout from '@/components/feature/ServicePageLayout';

const faqs = [
  {
    question: 'Şahıs şirketi kurmak için 2026\'da hangi belgeler gerekiyor?',
    answer: 'Nüfus cüzdanı fotokopisi, ikametgah belgesi, vergi dairesi başvuru formu ve imza beyannamesi temel belgelerdir. Bazı meslek grupları için ek belgeler (diploma, ruhsat vb.) talep edilebilir.',
  },
  {
    question: 'Şahıs şirketi kuruluşunun maliyeti ne kadar? (2026)',
    answer: '2026 yılı itibarıyla şahıs şirketi kuruluşu için noter masrafları, vergi levhası ve oda kaydı dahil toplam maliyet yaklaşık 3.000–6.000 TL arasında değişmektedir. Faaliyet alanına göre ek harçlar söz konusu olabilir.',
  },
  {
    question: 'Şahıs şirketi kuruluşu ne kadar sürer?',
    answer: 'Belgeler eksiksiz hazırlandığında vergi dairesi kaydı 1–2 iş günü içinde tamamlanabilir. SGK kaydı ve oda tescili dahil tüm süreç ortalama 3–5 iş günü sürmektedir.',
  },
  {
    question: 'Şahıs şirketi kurmak için şartlar nelerdir?',
    answer: 'T.C. vatandaşı olmak veya çalışma iznine sahip yabancı uyruklu olmak, 18 yaşını doldurmuş olmak ve kısıtlı olmamak temel şartlardır. Bazı meslekler için mesleki yeterlilik belgesi de aranmaktadır.',
  },
  {
    question: 'Şahıs şirketinde KDV mükellefi olmak zorunlu mu?',
    answer: 'Basit usul kapsamındaki mükellefler KDV\'den muaf tutulabilir. Gerçek usul kapsamındaki şahıs şirketleri ise KDV mükellefi olmak zorundadır. Hangi usule tabi olduğunuz faaliyet türüne ve cirosuna göre belirlenir.',
  },
  {
    question: 'Şahıs şirketini limited şirkete dönüştürebilir miyim?',
    answer: 'Evet, şahıs şirketini limited şirkete dönüştürmek mümkündür. Bu süreç nevi değişikliği olarak adlandırılır ve belirli yasal prosedürleri kapsar. Bir mali müşavir eşliğinde yürütülmesi önerilir.',
  },
];

const steps = [
  { num: '01', title: 'Vergi Dairesi Kaydı', desc: 'Bağlı olduğunuz vergi dairesine başvurarak vergi numarası alınır. Faaliyet konusu ve adresi bildirilir.' },
  { num: '02', title: 'İmza Beyannamesi', desc: 'Noterde imza beyannamesi düzenlenir. Bu belge resmi işlemlerde kullanılacaktır.' },
  { num: '03', title: 'SGK Kaydı', desc: 'Bağ-Kur (4/b) kapsamında SGK\'ya bildirim yapılır. Prim ödemeleri başlar.' },
  { num: '04', title: 'Oda / Birlik Kaydı', desc: 'Faaliyet alanına göre esnaf odası veya ticaret odası kaydı yapılır.' },
  { num: '05', title: 'Defter Tasdiki', desc: 'Gerçek usul mükellefleri için yasal defterler noterde tasdik ettirilir.' },
  { num: '06', title: 'Faaliyete Başlama', desc: 'Tüm kayıtlar tamamlandıktan sonra yasal olarak faaliyete başlanabilir.' },
];

const docs = [
  { icon: 'ri-id-card-line', title: 'Nüfus Cüzdanı', desc: 'Aslı ve fotokopisi' },
  { icon: 'ri-home-line', title: 'İkametgah Belgesi', desc: 'Son 3 ay içinde alınmış' },
  { icon: 'ri-file-text-line', title: 'Başvuru Formu', desc: 'Vergi dairesinden temin edilir' },
  { icon: 'ri-pen-nib-line', title: 'İmza Beyannamesi', desc: 'Noterde düzenlenir' },
  { icon: 'ri-map-pin-line', title: 'Kira Sözleşmesi', desc: 'İşyeri adresi için' },
  { icon: 'ri-camera-line', title: 'Fotoğraf', desc: '2 adet biyometrik' },
];

export default function SahisSirketiNasilKurulurPage() {
  const [income, setIncome] = useState(150000);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const taxCalc = () => {
    let tax = 0;
    const brackets = [
      { limit: 158000, rate: 0.15 },
      { limit: 330000, rate: 0.20 },
      { limit: 800000, rate: 0.27 },
      { limit: 4300000, rate: 0.35 },
      { limit: Infinity, rate: 0.40 },
    ];
    let remaining = income;
    let prev = 0;
    for (const b of brackets) {
      if (remaining <= 0) break;
      const chunk = Math.min(remaining, b.limit - prev);
      tax += chunk * b.rate;
      remaining -= chunk;
      prev = b.limit;
    }
    return Math.round(tax);
  };

  const tax = taxCalc();
  const net = income - tax;
  const effectiveRate = income > 0 ? ((tax / income) * 100).toFixed(1) : '0.0';

  return (
    <div className="min-h-screen bg-white">
      <InfoNavbar />

      {/* Hero */}
      <section className="relative pt-20 min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6cc8e012157b51fe5d48b60841db47f8.jpg"
            alt="Şahıs Şirketi Nasıl Kurulur 2026"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/92 via-[#0A2540]/75 to-[#0A2540]/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 w-full">
          <nav className="flex items-center gap-2 mb-5" aria-label="breadcrumb">
            <Link to="/" className="text-white/50 text-xs hover:text-white/80 transition-colors">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line text-white/30 text-xs"></i>
            <span className="text-white/70 text-xs">Şahıs Şirketi Nasıl Kurulur?</span>
          </nav>
          <div className="flex flex-wrap gap-2 mb-5">
            {['2026 Güncel', 'Nasıl Yapılır', 'Şartları', 'Maliyeti', 'Adım Adım'].map((kw) => (
              <span key={kw} className="px-3 py-1 bg-white/10 border border-white/20 text-white/80 text-xs rounded-full">{kw}</span>
            ))}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 max-w-2xl leading-tight">
            Şahıs Şirketi Nasıl Kurulur? (2026)
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed mb-8">
            Gerekli belgeler, başvuru adımları ve 2026 yılı güncel mevzuatına göre eksiksiz rehber.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="#iletisim-cta"
              onClick={(e) => { e.preventDefault(); document.getElementById('iletisim-cta')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-6 py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap"
            >
              Bilgi Al
            </a>
            <a
              href="https://wa.me/905300953230"
              target="_blank"
              rel="nofollow noreferrer"
              className="px-6 py-3 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-whatsapp-line"></i>WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <p className="text-gray-600 text-base leading-relaxed text-center">
            <strong>Şahıs şirketi</strong>, Türkiye'de en hızlı ve en düşük maliyetle kurulabilen işletme türüdür. 2026 yılı itibarıyla güncel mevzuata göre kuruluş süreci, gerekli belgeler ve dikkat edilmesi gereken noktalar bu rehberde sade bir dille anlatılmaktadır. Durumunuza özel bilgi almak için iletişime geçebilirsiniz.
          </p>
        </div>
      </section>

      {/* Gerekli Belgeler */}
      <section className="py-14 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Belgeler</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Gerekli Belgeler</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {docs.map((doc, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A2540]/5 rounded-xl flex-shrink-0">
                  <i className={`${doc.icon} text-[#0A2540] text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-[#0A2540] font-semibold text-sm mb-1">{doc.title}</h3>
                  <p className="text-gray-500 text-xs">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Adım Adım Süreç */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Süreç</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Adım Adım Kuruluş Süreci</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {steps.map((step, i) => (
              <div key={i} className="bg-[#F5F7FA] rounded-2xl p-6 border border-gray-100">
                <div className="text-4xl font-black text-[#0A2540]/8 mb-3">{step.num}</div>
                <h3 className="text-[#0A2540] font-semibold text-sm mb-2">{step.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini Hesaplama Aracı */}
      <section className="py-14 bg-[#F5F7FA]">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">Örnek Hesaplama</span>
            <h2 className="text-2xl font-bold text-[#0A2540] mt-3">Yaklaşık Vergi Yükü Hesabı</h2>
            <p className="text-gray-500 text-sm mt-2">Şahıs şirketinde yıllık gelirinize göre tahmini vergi yükünüzü görün.</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <label className="text-[#0A2540] text-sm font-medium">Yıllık Net Gelir</label>
                <span className="text-[#0A2540] font-bold text-sm">{income.toLocaleString('tr-TR')} ₺</span>
              </div>
              <input
                type="range"
                min={50000}
                max={2000000}
                step={10000}
                value={income}
                onChange={(e) => setIncome(Number(e.target.value))}
                className="w-full accent-[#0A2540]"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>50.000 ₺</span>
                <span>2.000.000 ₺</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Tahmini Vergi</p>
                <p className="text-[#0A2540] font-bold text-lg">{tax.toLocaleString('tr-TR')} ₺</p>
              </div>
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Net Kalan</p>
                <p className="text-emerald-600 font-bold text-lg">{net.toLocaleString('tr-TR')} ₺</p>
              </div>
              <div className="bg-[#F5F7FA] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Efektif Oran</p>
                <p className="text-[#0A2540] font-bold text-lg">%{effectiveRate}</p>
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-4 text-center">* Bu hesaplama tahmini olup bilgilendirme amaçlıdır. Kesin rakamlar için mali müşavirinize danışınız.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-[#0A2540]/50 uppercase">SSS</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mt-3">Sık Sorulan Sorular</h2>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      {/* Soft CTA */}
      <SoftCTA />

      {/* Contact CTA */}
      <div id="iletisim-cta">
        <ContactCTA />
      </div>

      <InfoFooter />

      <a
        href="https://wa.me/905300953230"
        target="_blank"
        rel="nofollow noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full cursor-pointer hover:scale-110 transition-transform"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
}

function FAQAccordion({ faqs }: { faqs: { question: string; answer: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqs.map((faq, i) => (
        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-[#F5F7FA] transition-colors cursor-pointer"
            onClick={() => setOpen(open === i ? null : i)}
          >
            <span className="text-[#0A2540] font-medium text-sm pr-4">{faq.question}</span>
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className={`text-[#0A2540] text-base transition-transform duration-200 ${open === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
            </div>
          </button>
          {open === i && (
            <div className="px-6 py-4 bg-[#F5F7FA] border-t border-gray-100">
              <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SoftCTA() {
  return (
    <section className="py-10 bg-[#F5F7FA]">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A2540]/5 rounded-full mb-4">
          <i className="ri-information-line text-[#0A2540] text-sm"></i>
          <span className="text-[#0A2540] text-xs font-medium">Bilgilendirme</span>
        </div>
        <p className="text-gray-600 text-sm leading-relaxed">
          Bu sayfadaki bilgiler genel rehber niteliğindedir. Durumunuza özel bilgi almak için iletişime geçebilirsiniz.
        </p>
      </div>
    </section>
  );
}

function ContactCTA() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return;
    if (form.message.length > 500) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({ name: form.name, phone: form.phone, message: form.message });
      const res = await fetch('https://readdy.ai/api/form/d7c144qqofe8vli40s7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setStatus('success'); setForm({ name: '', phone: '', message: '' }); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <section className="py-20 bg-[#0A2540]">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold tracking-widest text-[#60A5FA] uppercase">Ücretsiz Danışmanlık</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-4">Durumunuza Özel Bilgi Alın</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-6">Sorularınızı bize iletin, uzman ekibimiz en kısa sürede size dönüş yapsın.</p>
            <div className="flex flex-col gap-3">
              {[{ icon: 'ri-phone-line', text: '0 530 095 32 30' }, { icon: 'ri-mail-line', text: 'info@ozgurcakmak.com.tr' }, { icon: 'ri-time-line', text: 'Pzt–Cum: 09:00–18:00' }].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={handleSubmit} data-readdy-form id="info-contact-form" className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
            <div>
              <label className="text-white/60 text-xs mb-1.5 block">Ad Soyad</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Adınız Soyadınız" className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors" />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1.5 block">Telefon</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="+90 5XX XXX XX XX" className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors" />
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <label className="text-white/60 text-xs">Mesajınız</label>
                <span className="text-white/30 text-xs">{form.message.length}/500</span>
              </div>
              <textarea name="message" value={form.message} onChange={handleChange} required maxLength={500} rows={4} placeholder="Danışmak istediğiniz konu..." className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-white/40 transition-colors resize-none"></textarea>
            </div>
            {status === 'success' && <div className="flex items-center gap-2 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg"><i className="ri-checkbox-circle-line text-emerald-400"></i><span className="text-emerald-300 text-sm">Mesajınız iletildi.</span></div>}
            {status === 'error' && <div className="p-3 bg-rose-500/20 border border-rose-500/30 rounded-lg"><span className="text-rose-300 text-sm">Bir hata oluştu. Lütfen tekrar deneyin.</span></div>}
            <button type="submit" disabled={status === 'loading' || form.message.length > 500} className="w-full py-3 bg-white text-[#0A2540] text-sm font-semibold rounded-lg hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {status === 'loading' ? 'Gönderiliyor...' : 'Mesaj Gönder'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoNavbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white border-b border-gray-100' : 'bg-[#0A2540]'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center cursor-pointer">
          <img src="https://storage.readdy-site.link/project_files/20f355b3-57c6-42af-8f1b-e19ef75cee72/a013bf3f-953f-4147-a966-2d3225ff6ac1_zgr-akmak-logo-3.png?v=001ea3c7f21fa91e8ebb91c8becb8de9" alt="Özgür Çakmak SMMM Logo" className="h-12 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#0A2540]' : 'text-white/80'}`}>Ana Sayfa</Link>
          <a href="#iletisim-cta" onClick={(e) => { e.preventDefault(); document.getElementById('iletisim-cta')?.scrollIntoView({ behavior: 'smooth' }); }} className="px-5 py-2 bg-[#0A2540] text-white text-sm font-medium rounded-lg hover:-translate-y-0.5 transition-all whitespace-nowrap cursor-pointer border border-white/20">İletişime Geç</a>
        </div>
      </div>
    </header>
  );
}

function InfoFooter() {
  const infoLinks = [
    { label: 'Şahıs Şirketi Nasıl Kurulur?', href: '/sahis-sirketi-nasil-kurulur' },
    { label: 'Vergi Oranları 2026', href: '/sahis-sirketi-vergi-oranlari' },
    { label: 'Limited mi Şahıs mı?', href: '/limited-mi-sahis-mi' },
    { label: 'E-Fatura Zorunluluğu', href: '/e-fatura-kimler-icin-zorunlu' },
    { label: 'KDV Nedir?', href: '/kdv-nedir-nasil-hesaplanir' },
    { label: 'Vergi Borcu Sorgulama', href: '/vergi-borcu-sorgulama' },
  ];
  return (
    <footer className="bg-[#F5F7FA] border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center mb-3">
              <img src="https://storage.readdy-site.link/project_files/20f355b3-57c6-42af-8f1b-e19ef75cee72/a013bf3f-953f-4147-a966-2d3225ff6ac1_zgr-akmak-logo-3.png?v=001ea3c7f21fa91e8ebb91c8becb8de9" alt="Özgür Çakmak SMMM Logo" className="h-10 w-auto object-contain" />
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">Mali süreçlerinizde güvenilir danışmanlık.</p>
          </div>
          <div>
            <h4 className="text-[#0A2540] font-semibold text-sm mb-3">Bilgi Merkezi</h4>
            <ul className="space-y-2">
              {infoLinks.map((l) => (
                <li key={l.href}><Link to={l.href} className="text-gray-500 text-xs hover:text-[#0A2540] transition-colors cursor-pointer">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-[#0A2540] font-semibold text-sm mb-3">İletişim</h4>
            <div className="space-y-2">
              {[{ icon: 'ri-phone-line', text: '0 530 095 32 30' }, { icon: 'ri-mail-line', text: 'info@ozgurcakmak.com.tr' }].map((item) => (
                <div key={item.text} className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400"><i className={`${item.icon} text-sm`}></i></div>
                  <span className="text-gray-500 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-gray-400 text-xs">© 2026 Akın SMMM. Tüm hakları saklıdır.</p>
          <Link to="/" className="text-gray-400 text-xs hover:text-gray-600 transition-colors cursor-pointer">Ana Sayfaya Dön</Link>
        </div>
      </div>
    </footer>
  );
}
